/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useReducer } from "react";
import { useOutletContext, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAdminUpdateUser, useAdminGetUser, useAdminReivite, useAdminUploadPicture, useAdminDeletePicture } from "@/services/userService";
import { useAdminGetRegionsSWR } from "@/services/regionService";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { compareTwoArrays } from "@/utils/compareTwoArrays";
import { ArrowBigRight, Trash, Save, Check, X, Mail } from "lucide-react";
import { useAdminGetRolesSWR, useAdminGetRolesDefinitionsSWR } from "@/services/roleService";
import { useAdminGetSkillsSWR } from "@/services/skillService";
import { useAdminGetCompetitionsSWR } from "@/services/competitionService";
import { apiURL } from "@/services/apiService";
import { toInitials } from "@/utils/toInitials";

/* DayJs */
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import localizedFormat from "dayjs/plugin/localizedFormat"
import utc from "dayjs/plugin/utc"

import "dayjs/locale/fr"

dayjs.locale("fr")
dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)
dayjs.extend(utc)


const initialState = {
    apiPayload: {
        first_name: '',
        last_name: '',
        username: '',
        email: '',
        email_digest: '',
        password: '',
        enabled: true,
        role_ids: [],
        on_keycloak: true,
        on_inscriptions_plateform: false,
        whoiswho_hide_picture: false,
        whoiswho_hide_email: false,
        hidden: false,
    },
    initialApiPayload: {
        first_name: '',
        last_name: '',
        username: '',
        email: '',
        email_digest: '',
        password: '',
        enabled: true,
        role_ids: [],
        on_keycloak: true,
        on_inscriptions_plateform: false,
        whoiswho_hide_picture: false,
        whoiswho_hide_email: false,
        hidden: false,
    },
    roles: [],
    role_filter: {
        type: '',
        skill_id: '',
        competition_id: '',
    },
    selected_role: "",
    selected_region: "",
    error_message: "",
    isLoading: false
}



const ACTIONS = {
    SET_API_PAYLOAD_FIELD: 'setApiPayloadField',
    SET_INITIAL_API_PAYLOAD: 'setInitialApiPayload',
    SET_INITIAL_API_PAYLOAD_PICTURE: 'setInitialApiPayloadPicture',
    SET_ROLE_FILTER: 'setRoleFilter',
    SET_SELECTED_ROLE: 'setSelectedRole',
    SET_SELECTED_REGION: 'setSelectedRegion',
    ADD_ROLE: 'addRole',
    REMOVE_ROVE: 'removeRole',
    SET_ERROR_MESSAGE: 'setErrorMessage',
    SET_IS_LOADING: 'setIsLoading',
}



const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_API_PAYLOAD_FIELD:
            return { ...state, apiPayload: { ...state.apiPayload, [action.subtype]: action.payload }, error_message: null }
        case ACTIONS.SET_INITIAL_API_PAYLOAD:
            return { ...state, apiPayload: action.payload, initialApiPayload: action.payload, error_message: null }
        case ACTIONS.SET_INITIAL_API_PAYLOAD_PICTURE:
            return { ...state, apiPayload: { ...state.apiPayload, picture_url: action.payload }, initialApiPayload: { ...state.initialApiPayload, picture_url: action.payload }, error_message: null }
        case ACTIONS.SET_ROLE_FILTER:
            return { ...state, role_filter: { ...state.role_filter, [action.subtype]: action.payload } }
        case ACTIONS.SET_SELECTED_ROLE:
            return { ...state, selected_role: action.payload }
        case ACTIONS.SET_SELECTED_REGION:
            return { ...state, selected_region: action.payload }
        case ACTIONS.ADD_ROLE:
            return { ...state, apiPayload: { ...state.apiPayload, role_ids: [...state.apiPayload.role_ids, { role_id: Number(state.selected_role), region_id: Number(state.selected_region) || null} ] } }
        case ACTIONS.REMOVE_ROVE:
            return { ...state, apiPayload: { ...state.apiPayload, role_ids: state.apiPayload.role_ids.filter((role_ass) => role_ass.role_id != Number(action.payload)) } }
        case ACTIONS.SET_ERROR_MESSAGE:
            return { ...state, error_message: action.payload }
        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload }
        default:
            return state
    }
}



export default function Add() {

    const [ , setPageTitle,  , setShowBackButton] = useOutletContext()
    const [state, dispatch] = useReducer(reducer, initialState)

    const {id} = useParams()

    const navigate = useNavigate()
    const location = useLocation()

    const {data: roles } = useAdminGetRolesSWR()
    const {data: rolesDefinitions } = useAdminGetRolesDefinitionsSWR()
    const {data: skills } = useAdminGetSkillsSWR()
    const {data: competitions } = useAdminGetCompetitionsSWR()
    const {data: regions } = useAdminGetRegionsSWR()
    const adminUpdateUser = useAdminUpdateUser()
    const adminGetUser = useAdminGetUser()
    const adminReivite = useAdminReivite()
    const adminUploadPicture = useAdminUploadPicture()
    const adminDeletePicture = useAdminDeletePicture()


    /* On récupère l'utilisateur automatiquement lorsque l'id est disponible */
    useEffect(() => {

        const getUser = async () => {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

            try {
                const user = await adminGetUser(id)
                user.role_ids = user.roles.map((role) => {
                    return {role_id: role.id, region_id: regions.find((region) => region?.code === role.region?.code)?.id || null}
                })
                user.password = ''
                dispatch({ type: ACTIONS.SET_INITIAL_API_PAYLOAD, payload: user })
            } catch (error) {
                console.error('admin/users/[id]::getUser::error', error)
                if (isAxiosError(error)) {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
                } else {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
                }
            } finally {
                dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
            }
        }

        if (id && regions) {
            getUser()
        }
    }, [id, regions])



    /* On met à jour le titre et le bouton de retour */
    useEffect(() => {
        setPageTitle("Modification d'un utilisateur")
        setShowBackButton(true)

        return () => {
            setPageTitle("Titre")
            setShowBackButton(false)
        }
    }, [setPageTitle, setShowBackButton])



    /* On met à jour le titre du formulaire lorsque les informations de l'utilisateur sont disponibles */
    useEffect(() => {
        setPageTitle(`Modification d'un utilisateur : ${state.initialApiPayload.first_name} ${state.initialApiPayload.last_name}`)
    }, [setPageTitle, state.initialApiPayload])



    /* Gestion des changements de champs du formulaire a l'exception des rôles */
    const handleChange = (e) => {
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });
        dispatch({ type: ACTIONS.SET_API_PAYLOAD_FIELD, subtype: e.target.name, payload: e.target.type == 'checkbox' ? e.target.checked : e.target.value })
    }



    /* Soumission du formulaire */
    const handleSubmit = async (e) => {
        e.preventDefault()

        if(!state.apiPayload.first_name || !state.apiPayload.last_name ) {
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Veuillez remplir tous les champs obligatoires" });
            return 
        }

        const payload = Object.fromEntries(Object.entries(state.apiPayload).filter(([key, value]) => {
            if (key == 'role_ids') {
                return !compareTwoArrays(value, state.initialApiPayload.role_ids)
            } else {
                return value != state.initialApiPayload[key]
            }
        }))
        

        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });

        try {
            await adminUpdateUser(id, payload)
            navigate(location.state?.from ?? "/admin/users")
        } catch (error) {
            console.error('admin/users/add::handleSubmit::error', error)
            if (error?.response?.status === 412) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Erreur de validation en base de données (username ou email déjà utilisé, contrainte non respectée)" });
            } else if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    }


    /* Réinvitation du compte */
    const handleReinvite = async (e, resetPassword) => {
        e.preventDefault()

        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });

        try {
            await adminReivite(id, resetPassword)
        } catch (error) {
            console.error('admin/users/add::handleReinvite::error', error)
            if (error?.response?.status === 412) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Erreur de validation en base de données (username ou email déjà utilisé, contrainte non respectée)" });
            } else if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    }



    /* Gestion du changement de photo */
    const handleChangePicture = async (file) => {
        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
        try {
            await adminUploadPicture(id, file)
            const user = await adminGetUser(id)
            dispatch({ type: ACTIONS.SET_INITIAL_API_PAYLOAD_PICTURE, payload: user.picture_url })
        } catch (error) {
            console.error('admin/users/[id]::handleChangePicture::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    }



    /* Gestion de la suppression de la photo */
    const handleDeletePicture = async () => {
        try {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
            await adminDeletePicture(id)
            const user = await adminGetUser(id)
            dispatch({ type: ACTIONS.SET_INITIAL_API_PAYLOAD_PICTURE, payload: user.picture_url })
        } catch (error) {
            console.error('admin/users/[id]::handleDeletePicture::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    }



    return (
        <>
            <div className="flex flex-row justify-between items-center">
                <h2 className="text-secondary text-lg flex-none content-center mb-4">
                    Paramètres de l&apos;utilisateur <span className="text-xs">{id && `ID:${id}`}</span>
                </h2>

                <div className="flex flex-row gap-3 items-center">
                    
                    <label className="text-blue-500 text-sm underline hover:text-blue-800 cursor-pointer" htmlFor="picture">
                        Modifier
                        <input type="file" className="hidden" id="picture" onChange={async (e) => {
                            if (e.target.files.length > 0) {
                                await handleChangePicture(e.target.files[0]);
                            }
                        }}/>
                    </label>

                    { state.initialApiPayload.picture_url && 
                        <button className="text-sm text-red-500 hover:text-red-800 cursor-pointer" onClick={(e) => handleDeletePicture(e)}>
                            <Trash size={12} />
                        </button> 
                    }

                    { state.initialApiPayload.picture_url
                        ? <img src={apiURL + state.initialApiPayload.picture_url} alt="Photo de l'utilisateur" className="rounded-full w-16 h-16 border-2 border-secondary" 
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={async (e) => {
                                e.preventDefault();
                                if (e.dataTransfer.files.length > 0) {
                                    await handleChangePicture(e.dataTransfer.files[0]);
                                }
                            }}
                        />
                        : <div className="rounded-full bg-pink-400 w-16 h-16 text-xl flex items-center justify-center border-2 border-secondary"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={async (e) => {
                                e.preventDefault();
                                if (e.dataTransfer.files.length > 0) {
                                    await handleChangePicture(e.dataTransfer.files[0]);
                                }
                            }}
                        
                        >
                            {toInitials(state.initialApiPayload.first_name, state.initialApiPayload.last_name)}
                        </div>
                    }
                </div>
            </div>

            <form className="flex flex-col gap-4" onSubmit={(e) => handleSubmit(e)}>
                <div className="w-full flex flex-col flex-wrap gap-4">

                    {/* Premiere ligne : username et password */}
                    <div className="md:w-[100%] flex flex-row flex-wrap gap-5 content-stretch">

                        <div className="flex flex-col gap-2 flex-1">
                            <label className="text-primary text-base" htmlFor="username">
                                Nom d&apos;utilisateur {state.apiPayload.on_keycloak && <span className="italic text-xs text-red-500">Cela peut casser le lien de l&apos;utilisateur avec d&apos;autres applications</span>}
                            </label>
                            <input type="text" 
                                className={`w-full p-1 border ${ state?.apiPayload?.username != state?.initialApiPayload?.username ? "border-red-600" : "border-secondary" } rounded-md disabled:bg-slate-200`} 
                                id="username" name="username" 
                                value={state.apiPayload.username} 
                                onChange={handleChange}
                                disabled={state.apiPayload.on_keycloak}
                                readOnly={state.apiPayload.on_keycloak}
                                 />
                        </div>

                        <div className="flex flex-col gap-2 flex-1">
                            <label className="text-primary text-base" htmlFor="password">
                                Password {state.apiPayload.on_keycloak && <span className="italic text-xs">(réinitialisera pour toutes les applications Worldskills)</span>}
                            </label>
                            <input type="password" 
                                className={`w-full p-1 border ${ state?.apiPayload?.password != state?.initialApiPayload?.password ? "border-red-600" : "border-secondary" } rounded-md`} 
                                required
                                id="password" name="password" 
                                value={state.apiPayload.password} 
                                onChange={handleChange} />
                        </div> 
                    </div>

                    {/* Deuxième ligne : prénom et nom */}
                    <div className="md:w-[100%] flex flex-row flex-wrap gap-5 content-stretch">
                        <div className="flex flex-col gap-2 flex-1">
                            <label className="text-primary text-base" htmlFor="first_name">
                                Prénom <span className="text-red-500">*</span>
                            </label>
                            <input type="text" 
                                className={`w-full p-1 border ${ state?.apiPayload?.first_name != state?.initialApiPayload?.first_name ? "border-red-600" : "border-secondary" } rounded-md`} 
                                required
                                id="first_name" name="first_name" 
                                value={state.apiPayload.first_name} 
                                onChange={handleChange} />
                        </div>
                        <div className="flex flex-col gap-2 flex-1">
                            <label className="text-primary text-base" htmlFor="last_name">
                                Nom <span className="text-red-500">*</span>
                            </label>
                            <input type="text" 
                                className={`w-full p-1 border ${ state?.apiPayload?.last_name != state?.initialApiPayload?.last_name ? "border-red-600" : "border-secondary" } rounded-md`} 
                                required
                                id="last_name" name="last_name" 
                                value={state.apiPayload.last_name} 
                                onChange={handleChange} />
                        </div>    
                    </div>
                    
                    {/* Troisieme ligne : email */}
                    <div className="md:w-[60%] flex flex-row flex-wrap gap-5 content-stretch">
                        <div className="flex flex-col gap-2 flex-auto">
                            <label className="text-primary text-base" htmlFor="email">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <input type="text"
                                className={`w-full p-1 border ${ state?.apiPayload?.email != state?.initialApiPayload?.email ? "border-red-600" : "border-secondary" } rounded-md disabled:bg-gray-200`}
                                required
                                id="email" name="email" 
                                value={state.apiPayload.email} 
                                onChange={handleChange} 
                                disabled={state?.apiPayload?.on_inscriptions_plateform}/>
                        </div>  
                    </div>

                    {/* Troisieme ligne : email_digest */}
                    <div className="md:w-[60%] flex flex-row flex-wrap gap-5 content-stretch">
                        <div className="flex flex-col gap-2 flex-auto">
                            <label className="text-primary text-base" htmlFor="email">
                                Email pour les résumés
                            </label>
                            <input type="text"
                                className={`w-full p-1 border ${ state?.apiPayload?.email_digest != state?.initialApiPayload?.email_digest ? "border-red-600" : "border-secondary" } rounded-md disabled:bg-gray-200`}
                                required
                                id="email_digest" name="email_digest" 
                                value={state.apiPayload.email_digest || ""} 
                                onChange={handleChange} />
                        </div>  
                    </div>

                    {/* Quatrième ligne : activé */}
                    <div className="flex flex-row gap-5">
                        <div className="flex flex-row gap-2 items-center">
                            
                            <input type="checkbox" 
                                className={`h-6 w-6 p-1 border ${ state?.apiPayload?.enabled != state?.initialApiPayload?.enabled ? "border-red-600" : "border-secondary" } rounded-md`} 
                                required
                                id="enabled" name="enabled" 
                                checked={state.apiPayload.enabled} 
                                onChange={handleChange} />

                            <label className="text-primary text-base content-center" htmlFor="enabled">
                                Activé
                            </label>
                        </div>
                    </div>

                    {/* Quatrième ligne : Caché du WhoIsWho */}
                    <div className="flex flex-row gap-5">
                        <div className="flex flex-row gap-2 items-center">
                            
                            <input type="checkbox" 
                                className={`h-6 w-6 p-1 border ${ state?.apiPayload?.hidden !== state?.initialApiPayload?.hidden ? "border-red-600" : "border-secondary" } rounded-md`} 
                                required
                                id="hidden" name="hidden" 
                                checked={state.apiPayload.hidden} 
                                onChange={handleChange} />

                            <label className="text-primary text-base content-center" htmlFor="hidden">
                                Caché du Who is Who
                            </label>
                        </div>
                    </div>

                    {/* Ligne : attaché à keycloak */}
                    <div className="flex flex-row gap-5">
                        <div className="flex flex-row gap-2 items-center">
                            
                            <input type="checkbox" className="h-6 w-6 border border-secondary rounded-md content-center"
                                required
                                id="on_keycloak" name="on_keycloak" 
                                checked={state.apiPayload.on_keycloak} 
                                onChange={handleChange} />

                            <label className="text-primary text-base content-center" htmlFor="on_keycloak">
                                Attaché à Keycloak <span className="italic text-sm">(si activé, l&apos;utilisateur devra passer par Keycloak pour se connecter)</span>
                            </label>
                        </div>
                    </div>

                    {/* Ligne : attaché à la plateforme d'inscription */}
                    <div className="flex flex-row gap-5">
                        <div className="flex flex-row gap-2 items-center">
                            
                            <input type="checkbox" className="h-6 w-6 border border-secondary rounded-md content-center"
                                required
                                id="on_inscriptions_plateform" name="on_inscriptions_plateform" 
                                checked={state.apiPayload.on_inscriptions_plateform} 
                                onChange={handleChange} />

                            <label className="text-primary text-base content-center" htmlFor="on_inscriptions_plateform">
                                Attaché à la plateforme d&apos;inscription <span className="italic text-sm">(si activé, email non modifiable)</span>
                            </label>
                        </div>
                    </div>

                    {/* Qui est qui */}
                    <div className="flex flex-row gap-5">
                        <div className="flex flex-row gap-2 items-center">
                            
                            <input type="checkbox" className="h-6 w-6 border border-secondary rounded-md content-center"
                                required
                                id="whoiswho_hide_picture" name="whoiswho_hide_picture" 
                                checked={state.apiPayload.whoiswho_hide_picture} 
                                onChange={handleChange} />

                            <label className="text-primary text-base content-center" htmlFor="whoiswho_hide_picture">
                                Masquer la photo de profil dans le Who Is Who
                            </label>
                        </div>
                    </div>
                    <div className="flex flex-row gap-5">
                        <div className="flex flex-row gap-2 items-center">
                            
                            <input type="checkbox" className="h-6 w-6 border border-secondary rounded-md content-center"
                                required
                                id="whoiswho_hide_email" name="whoiswho_hide_email" 
                                checked={state.apiPayload.whoiswho_hide_email} 
                                onChange={handleChange} />

                            <label className="text-primary text-base content-center" htmlFor="whoiswho_hide_email">
                                Masquer l&apos;email dans le Who Is Who
                            </label>
                        </div>
                    </div>

                    <div className="flex flex-row gap-5 w-full">
                        {/* Informations Keycloak */}
                        { state.apiPayload?.keycloak && <div className="md:w-[50%] flex flex-col text-xs flex-wrap gap-1 bg-slate-100 rounded-md p-2 text-secondary">
                            <h3 className="text-sm">Informations Keycloak</h3>
                            <p className="flex flex-row items-center gap-2">Trouvé dans keycloak : {state.apiPayload.keycloak.found ? <Check size={12} className="text-green-500" /> : <X size={12} className="text-red-500" />}</p>
                            <p className="flex flex-row items-center gap-2">Email : {state.apiPayload.keycloak.email}</p>
                            <p className="flex flex-row items-center gap-2">Email vérifé : {state.apiPayload.keycloak.email_verified ? <Check size={12} className="text-green-500" /> : <X size={12} className="text-red-500" />}</p>
                            <p className="flex flex-row items-center gap-2">Nom : {state.apiPayload.keycloak.first_name} {state.apiPayload.keycloak.last_name}</p>
                            <p className="flex flex-row items-center gap-2">Activé : {state.apiPayload.keycloak.enabled ? <Check size={12} className="text-green-500" /> : <X size={12} className="text-red-500" />}</p>
                        </div>}

                        {/* Informations Autres */}
                        { <div className="md:w-[50%] flex flex-col text-xs flex-wrap gap-1 bg-slate-100 rounded-md p-2 text-secondary">
                            <h3 className="text-sm">Autres informations</h3>
                            <p className="flex flex-row items-center gap-2">Date de création : {state?.apiPayload?.creation_date ? dayjs.utc(state.apiPayload.creation_date).local().format('LLL') + " (" + dayjs.utc(state.apiPayload.creation_date).fromNow() + ")" : "N/A"}</p>
                            <p className="flex flex-row items-center gap-2">Dernier login (hors SSO) : {state?.apiPayload?.last_login ? dayjs.utc(state.apiPayload.last_login).local().format('LLL') + " (" + dayjs.utc(state.apiPayload.last_login).fromNow() + ")" : "N/A"}</p>
                            <p className="flex flex-row items-center gap-2">Dernière visite : {state.apiPayload.last_seen ? dayjs.utc(state.apiPayload.last_seen).local().format('LLL') + " (" + dayjs.utc(state.apiPayload.last_seen).fromNow() + ")" : "N/A"}</p>
                            <p className="flex flex-row items-center gap-2">Dernière invitation : {state.apiPayload.last_invite ? dayjs.utc(state.apiPayload.last_invite).local().format('LLL') + " (" + dayjs.utc(state.apiPayload.last_invite).fromNow() + ")" : "N/A"}</p>
                        </div>}
                    </div>


                    {/* Derniere ligne : gestion des rôles */}
                    <div className="w-full flex flex-row gap-5 content-stretch">
                        <div className="flex flex-col flex-1 border border-secondary rounded-md px-2 py-1 bg-gray-100 gap-3">
                            <h3 className="text-secondary">Ajouter un role</h3>
                            <div className="w-full flex flex-row items-center text-sm">
                                <div className="flex flex-col flex-grow gap-2">
                                    <div className="text-primary flex flex-row flex-wrap items-center gap-1">
                                        <label>
                                            Type :
                                        </label>
                                        <select className="border-0 py-0 text-sm"
                                            onChange={(e) => dispatch({ type: ACTIONS.SET_ROLE_FILTER, subtype: "type", payload: e.target.value })} 
                                            value={state.role_filter.type}>

                                            <option value="">-</option>
                                            {rolesDefinitions && Object.keys(rolesDefinitions)?.map((role, i) => {
                                                return (
                                                    <option key={i} value={role}>
                                                        {role}
                                                    </option>
                                                )
                                            })}
                                        </select>
                                    </div>
                                    <div className="text-primary flex flex-row flex-wrap items-center gap-1">
                                        <label>
                                            Métier :
                                        </label>
                                        <select className="border-0 py-0 text-sm"
                                            onChange={(e) => dispatch({ type: ACTIONS.SET_ROLE_FILTER, subtype: "skill_id", payload: e.target.value })} 
                                            value={state.role_filter.skill_id}>

                                            <option value="">-</option>
                                            {skills?.map((skill, i) => {
                                                return (
                                                    <option key={i} value={skill.id}>
                                                        {skill.code} - {skill.name}
                                                    </option>
                                                )
                                            })}
                                        </select>
                                    </div>
                                    <div className="text-primary flex flex-row flex-wrap items-center gap-1">
                                        <label>
                                            Compétition :
                                        </label>
                                        <select className="border-0 py-0 text-sm"
                                            onChange={(e) => dispatch({ type: ACTIONS.SET_ROLE_FILTER, subtype: "competition_id", payload: e.target.value })} 
                                            value={state.role_filter.competition_id}>

                                            <option value="">-</option>
                                            {competitions?.map((competition, i) => {
                                                return (
                                                    <option key={i} value={competition.id}>
                                                        {competition.code} - {competition.name}
                                                    </option>
                                                )
                                            })}
                                        </select>
                                    </div>
                                    <div className="text-primary flex flex-row flex-wrap items-center gap-1">
                                        <label>
                                            Rôles possibles :
                                        </label>
                                        <select className="border-0 py-0 text-sm"
                                            onChange={(e) => dispatch({ type: ACTIONS.SET_SELECTED_ROLE, payload: e.target.value })} 
                                            value={state.selected_role}
                                        >
                                            <option value="">-</option>
                                            {roles?.filter((role) => { 
                                                let result = true
                                                if(state?.role_filter?.type && state?.role_filter?.type.length > 0)
                                                    result &&= role.type === state.role_filter.type

                                                if(state?.role_filter?.skill_id && state?.role_filter?.skill_id.length > 0)
                                                    result &&= role.skill_id === Number(state.role_filter.skill_id)

                                                if(state?.role_filter?.competition && state?.role_filter?.competition.length > 0)
                                                    result &&= role.competition === Number(state.role_filter.competition)
                                                
                                                if( state?.apiPayload?.role_ids?.filter((role_ass) => role_ass.role_id == role.id).length > 0) result = false

                                                return result

                                            })?.map((role, i) => {
                                                return (
                                                    <option key={i} value={role.id}>
                                                        {role.name}
                                                    </option>
                                                )
                                            })}
                                        </select>
                                        <select className="border-0 py-0 text-sm"
                                            onChange={(e) => dispatch({ type: ACTIONS.SET_SELECTED_REGION, payload: e.target.value })} 
                                            value={state.selected_region}
                                        >
                                            <option value="">-</option>
                                            {regions?.map((region, i) => {
                                                return (
                                                    <option key={i} value={region.id}>
                                                        {region.code}
                                                    </option>
                                                )
                                            })}
                                        </select>
                                    </div>
                                </div>
                                <div className="items-center">
                                    { state?.selected_role && <button
                                        onClick={(e) => {e.preventDefault();dispatch({ type: ACTIONS.ADD_ROLE, payload: state.selected_role })}}
                                    >
                                        <ArrowBigRight size={24} className="text-primary hover:text-blue-600" />
                                    </button> }
                                </div>
                            </div>

                        </div>
                        <div 
                            className={`flex flex-1 flex-col border 
                                ${ compareTwoArrays(state.apiPayload.role_ids, state.initialApiPayload.role_ids) ? "border-secondary" : "border-red-500" } 
                            rounded-md px-2 py-1 bg-gray-100 gap-3`}>
                            <h3 className="text-secondary">Roles selectionnés</h3>
                            <div className="w-full flex flex-row flex-wrap gap-2">
                                {roles && state.apiPayload.role_ids.map((role_ass, i) => {
                                    const role = roles?.find((role) => role.id === role_ass.role_id)
                                    const borderColor = role?.rights?.filter((right) => right.includes("_admin")).length > 0
                                        ? "border-red-500"
                                        : "border-blue-500";

                                    return ( 
                                        <div key={i} className={`${borderColor} text-sm border rounded-md px-2 py-1 flex items-center gap-1`}>
                                            {role.name} 
                                            {role_ass.region_id && <span className="text-secondary"> ({regions.find((region) => region.id === role_ass.region_id)?.code})</span>}
                                            <button
                                            onClick={(e) => {e.preventDefault();dispatch({ type: ACTIONS.REMOVE_ROVE, payload: role_ass.role_id })}}
                                            >
                                                <Trash size={14} className="text-red-500 hover:text-red-700" />
                                            </button>
                                        </div> 
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                    {/* Fin : sauvegarder et message d'erreur */}
                    <div className="w-full gap-5 flex flex-row items-center">
                        
                        <button className="flex gap-2 items-center border border-blue-500 rounded-md px-2 py-1 text-base hover:bg-blue-200"
                            onClick={(e) => handleSubmit(e)}
                            disabled={state.isLoading}
                        >
                            <Save size={24} className="text-primary" />
                            Sauvegarder
                        </button>

                        {
                            state?.error_message && <span className="text-red-700 flex-none content-center mr-4">{state.error_message}</span>
                        }

                        <button className="flex gap-2 items-center border border-gray-500 rounded-md px-2 py-1 text-base hover:bg-gray-200"
                            onClick={(e) => handleReinvite(e, false)}
                            disabled={state.isLoading}
                        >
                            <Mail size={24} className="text-primary" />
                            Réinviter
                        </button>

                        <button className="flex gap-2 items-center border border-gray-500 rounded-md px-2 py-1 text-base hover:bg-gray-200"
                            onClick={(e) => handleReinvite(e, true)}
                            disabled={state.isLoading}
                        >
                            <Mail size={24} className="text-primary" />
                            Réinviter avec reset de mot de passe
                        </button>

                    </div>
                </div>
            </form>

        </>
    )
}